<template #default>
    <form
        v-if="status.detail"
        @submit.prevent="workgroupUUID ? requestUpdate() : requestCreate()"
    >
        <b-form-group
            :label="$t('label.name')"
            label-cols-sm="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.name"
                :class="{ 'is-invalid': submitted && !$v.createForm.name.required }"
                autocomplete="off"
            ></b-form-input>
            <b-form-invalid-feedback v-if="submitted && !$v.createForm.name.required" class="text-right">
                {{ $t("message.requiredX", [$t("label.name")]) }}
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            label="Search User"
            label-cols-sm="4"
            label-align="left"
        >
            <div class="row">
                <div class="col pr-1">
                    <div class="position-relative">
                        <b-form-input
                            v-model="userSearchParameter"
                            type="text"
                            class="autocomplete-input"
                            :placeholder="$t('label.search')"
                            autocomplete="off"
                            @keydown.enter.prevent="userEmailSearch"
                            @input="userOptions = []"
                        />

                        <ul
                            v-if="userOptions.length > 0"
                            class="autocomplete-dropdown"
                        >
                            <li
                                v-for="item in userOptions"
                                :key="item.value"
                                class="autocomplete-option"
                                @click="selectUser(item)"
                            >
                                {{ item.text }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="col-auto pl-1">
                    <b-button
                        type="button"
                        variant="info"
                        class="w-100"
                        v-b-tooltip.hover
                        :title="$t('label.search')"
                        @click="userEmailSearch"
                    >
                        <i class="mdi mdi-magnify"></i>
                    </b-button>
                </div>
            </div>
        </b-form-group>
        <hr />
        <b-form-group
            v-if="userSelected.length > 0"
            label="Existing Users"
            label-cols-sm="4"
            label-align="left"
        >
            <ul class="list-unstyled mb-0">
                <li
                    v-for="(item, index) in userSelected"
                    :key="item.value"
                    class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center p-2 border rounded mb-1"
                >
                    <span class="text-break mr-sm-2 mb-2 mb-sm-0">{{ item.text }}</span>
                    <div class="d-flex align-items-center flex-shrink-0 align-self-end align-self-sm-center">
                        <!-- <b-form-checkbox v-model="item.isAdmin" switch class="mr-2">Admin</b-form-checkbox> -->
                        <b-button type="button" size="sm" variant="danger" @click="userSelected.splice(index, 1)">
                            <i class="mdi mdi-close"></i>
                        </b-button>
                    </div>
                </li>
            </ul>
        </b-form-group>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2 col-5 col-sm-2"
                @click="$emit('done')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                class="col-5 col-sm-2"
                :disabled="!status.save"
            >
                <span v-if="status.save">
                    {{ workgroupUUID ? $t('label.updateX', []) : $t('label.createX', []) }}
                </span>
                <div v-else>
                    <b-spinner small />
                </div>
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-3">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { workgroupMethods, lookupMethods } from "@/state/api";
import { WORKGROUP_MODULE } from "@/state/api/workgroup.state";

export default {
    props: {
        workgroupUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                name: { required },
            },
        };
    },
    async created() {
        await this.getWorkgroupData();
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
            userSearchParameter: "",
            userOptions: [],
            userSelected: [],
        };
    },
    methods: {
        ...workgroupMethods,
        ...lookupMethods,
        defaultForm() {
            return {
                uuid: "",
                name: "",
                usageType: 2, // 1 - Task, 2 - Orientation
                users: [],
            };
        },
        async getWorkgroupData() {
            if (this.workgroupUUID) {
                await this.getWorkgroup({ uuid: this.workgroupUUID })
                    .then((result) => {
                        if (result?.success) {
                            this.userSelected = this.data.workgroupUsers
                                .filter(item => item.user)
                                .map(item => ({
                                    value: item.user.uuid,
                                    text: (item.user.fullName || `${item.user.firstName || ''} ${item.user.lastName || ''}`.trim() || item.user.email) + (item.user.email && (item.user.fullName || item.user.firstName || item.user.lastName) ? ` (${item.user.email})` : ''),
                                    isAdmin: item.isAdmin ? true : false,
                                }));
                            this.createForm = {
                                uuid: this.data.uuid,
                                name: this.data.name,
                                usageType: 2,
                                users: [],
                            };
                        }
                    });
            }
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    users: this.userSelected.map(user => ({
                        uuid: user.value,
                        isAdmin: user.isAdmin ? 1 : 0,
                    })),
                };
                this.createWorkgroup({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    users: this.userSelected.map(user => ({
                        uuid: user.value,
                        isAdmin: user.isAdmin ? 1 : 0,
                    })),
                };
                this.updateWorkgroup({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        async userEmailSearch() {
            if (this.userSearchParameter.trim()) {
                await this.lookupUserEmail([this.userSearchParameter.trim()])
                    .then((result) => {
                        if (result?.success) {
                            this.userOptions = this.userEmailDdl.filter(item => !this.userSelected.some(user => user.value == item.value));
                        }
                    });
            } else {
                this.userOptions = [];
            }
        },
        selectUser(user) {
            if (!this.userSelected.some(item => item.value == user.value)) {
                this.userSelected.push({ ...user, isAdmin: false });
            }
            this.userSearchParameter = "";
            this.userOptions = [];
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.userSearchParameter = "";
            this.userOptions = [];
            this.userSelected = [];
            this.$v.$reset();
        },
    },
    computed: {
        ...mapState(WORKGROUP_MODULE, ['status']),
        userEmailDdl() {
            return this.$store.state.api.lookup.userEmailDdl || [];
        },
        data() {
            return this.$store.state.api.workgroup.details;
        },
    },
}
</script>
<style scoped>
.autocomplete-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    z-index: 1050;

    max-height: 200px;
    overflow-y: auto;

    background: white;
    border: 1px solid #ccc;
    border-radius: 0 0 6px 6px;

    list-style: none;
    padding: 0;
    margin: 0;

    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.autocomplete-option {
    padding: 10px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.autocomplete-option:hover {
    background-color: #f5f5f5;
}

.autocomplete-option:last-child {
    border-bottom: none;
}
@media (max-width: 575.98px) {
    .autocomplete-option {
        white-space: normal;
        overflow-wrap: anywhere;
    }
}
</style>